import type { Vec3 } from '../sim/mathx';

export type BearingPolicy = 'from-radar' | 'sun-relative' | 'fixed-deg' | 'intercept';
export type SelectPolicy = 'nearest' | 'highest-snr' | 'first-confirmed' | 'priority-class';

export interface CompanionConfig {
  standoffRadius: number;
  observeAlt: number;
  bearingPolicy: BearingPolicy;
  selectPolicy: SelectPolicy;
  tLost: number;
  minSeparation: number;
  reacquireWindow: number;
  /** Radar position used by `from-radar`; world ENU metres. */
  radarEnu: Vec3;
  /** ENU bearing (0=east, 90=north) used by `fixed-deg`. */
  fixedBearingDeg: number;
  /** ENU sun azimuth; `sun-relative` stations the scout opposite the sun. */
  sunAzimuthDeg: number;
  /** Constant-velocity lead applied to a live track, seconds. */
  predictionHorizon: number;
  /** Three-dimensional arrival tolerance, metres. */
  posTol: number;
}

export const DEFAULT_COMPANION_CONFIG: Readonly<CompanionConfig> = Object.freeze({
  standoffRadius: 150,
  observeAlt: 80,
  bearingPolicy: 'from-radar',
  selectPolicy: 'nearest',
  tLost: 4,
  minSeparation: 40,
  reacquireWindow: 6,
  radarEnu: Object.freeze({ x: 0, y: 0, z: 0 }),
  fixedBearingDeg: 0,
  sunAzimuthDeg: 180,
  predictionHorizon: 2,
  posTol: 10,
});

export class CompanionConfigError extends Error {
  constructor(
    readonly path: string,
    message: string,
  ) {
    super(`companion config: ${path} ${message}`);
    this.name = 'CompanionConfigError';
  }
}

const BEARING_POLICIES: readonly BearingPolicy[] = [
  'from-radar',
  'sun-relative',
  'fixed-deg',
  'intercept',
];
const SELECT_POLICIES: readonly SelectPolicy[] = [
  'nearest',
  'highest-snr',
  'first-confirmed',
  'priority-class',
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const finiteOr = (value: unknown, fallback: number, path: string): number => {
  if (value === undefined) return fallback;
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new CompanionConfigError(path, 'must be a finite number');
  }
  return value;
};

const positiveOr = (value: unknown, fallback: number, path: string): number => {
  const parsed = finiteOr(value, fallback, path);
  if (parsed <= 0) throw new CompanionConfigError(path, 'must be > 0');
  return parsed;
};

const nonNegativeOr = (value: unknown, fallback: number, path: string): number => {
  const parsed = finiteOr(value, fallback, path);
  if (parsed < 0) throw new CompanionConfigError(path, 'must be >= 0');
  return parsed;
};

const enumOr = <T extends string>(
  value: unknown,
  fallback: T,
  allowed: readonly T[],
  path: string,
): T => {
  if (value === undefined) return fallback;
  if (typeof value !== 'string' || !allowed.includes(value as T)) {
    throw new CompanionConfigError(path, `must be one of ${allowed.join(', ')}`);
  }
  return value as T;
};

const vec3Or = (value: unknown, fallback: Readonly<Vec3>, path: string): Vec3 => {
  if (value === undefined) return { ...fallback };
  if (!isRecord(value)) throw new CompanionConfigError(path, 'must be an object {x,y,z}');
  return {
    x: finiteOr(value.x, fallback.x, `${path}.x`),
    y: finiteOr(value.y, fallback.y, `${path}.y`),
    z: finiteOr(value.z, fallback.z, `${path}.z`),
  };
};

/** Validate a JSON-shaped partial config and fill deterministic Phase-1 defaults. */
export const parseCompanionConfig = (value: unknown = {}): CompanionConfig => {
  if (!isRecord(value)) throw new CompanionConfigError('root', 'must be an object');
  const d = DEFAULT_COMPANION_CONFIG;
  return {
    standoffRadius: positiveOr(value.standoffRadius, d.standoffRadius, 'standoffRadius'),
    observeAlt: positiveOr(value.observeAlt, d.observeAlt, 'observeAlt'),
    bearingPolicy: enumOr(value.bearingPolicy, d.bearingPolicy, BEARING_POLICIES, 'bearingPolicy'),
    selectPolicy: enumOr(value.selectPolicy, d.selectPolicy, SELECT_POLICIES, 'selectPolicy'),
    tLost: nonNegativeOr(value.tLost, d.tLost, 'tLost'),
    minSeparation: positiveOr(value.minSeparation, d.minSeparation, 'minSeparation'),
    reacquireWindow: nonNegativeOr(value.reacquireWindow, d.reacquireWindow, 'reacquireWindow'),
    radarEnu: vec3Or(value.radarEnu, d.radarEnu, 'radarEnu'),
    fixedBearingDeg: finiteOr(value.fixedBearingDeg, d.fixedBearingDeg, 'fixedBearingDeg'),
    sunAzimuthDeg: finiteOr(value.sunAzimuthDeg, d.sunAzimuthDeg, 'sunAzimuthDeg'),
    predictionHorizon: nonNegativeOr(
      value.predictionHorizon,
      d.predictionHorizon,
      'predictionHorizon',
    ),
    posTol: positiveOr(value.posTol, d.posTol, 'posTol'),
  };
};
